import { Typography } from '@mui/material'
import React from 'react'
import { VariableSizeList, ListChildComponentProps } from 'react-window'

import { useHeightMeasurement } from 'hooks/use-height-measurement'
import { useResetCache } from 'hooks/use-reset-cache'
import theme from 'theme'

// using the value from the MUI example
const LISTBOX_PADDING = 8

/**
 * Render each record in the autocomplete dropdown
 * They can be either a group name or a selectable search option
 * This passes down important styles from react-window that make the virtualization work
 */
const VirtualizedListbox = React.forwardRef<HTMLLIElement>(
  (props: ListChildComponentProps, ref) => {
    const { data, index, style, children } = props

    const rowItem = data[index]
    const inlineStyle = {
      ...style,
      top: Number(style.top) + LISTBOX_PADDING
    }

    if (rowItem.hasOwnProperty('group')) {
      return (
        <li ref={ref} css={inlineStyle}>
          <Typography
            color="primary"
            data-test-autocomplete-target-group-name={rowItem.group}
            sx={{
              paddingBottom: 1,
              paddingLeft: 2,
              paddingTop: 2
            }}
          >
            {rowItem.group}
          </Typography>
        </li>
      )
    }

    return children
  }
)

VirtualizedListbox.displayName = 'VirtualizedListbox'

// this is from the MUI example
// it seems to be used to pass dynamic values down into the outer component of the virtualized list
const OuterElementContext = React.createContext({})

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext)
  return <div ref={ref} {...props} {...outerProps} />
})

const virtualizedOptionsDefaults = {
  // how many items we want react-window to render
  numItemsInWindow: 8,
  // how many items above and below the window we want react-window to pre-render so it updates quickly when scrolling
  overscanCount: 16,
  // assume initial size of list items and group labels
  // but also measure them to ensure we have accurate values
  assumedItemSize: 36,
  assumedGroupLabelSize: 48
}

// Adapter for react-window
// taken from MUI virtualization example
export const VirtualizedListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(
  props: { children: React.ReactChild[]; [key: string | number]: any },
  ref
) {
  const {
    children,
    OptionComponent,
    virtualizedOptions = virtualizedOptionsDefaults,
    ...otherProps
  } = props

  const itemData: React.ReactChild[] = []

  children.forEach(
    (item: React.ReactChild & { children?: React.ReactChild[] }) => {
      itemData.push(item)
      itemData.push(...(item.children || []))
    }
  )
  const {
    numItemsInWindow,
    overscanCount,
    assumedItemSize,
    assumedGroupLabelSize
  } = virtualizedOptions

  const itemCount = itemData.length

  const { height: itemSize, measurementRef: itemRef } =
    useHeightMeasurement<HTMLDivElement>(assumedItemSize)

  const { height: groupLabelSize, measurementRef: groupLabelRef } =
    useHeightMeasurement<HTMLDivElement>(assumedGroupLabelSize)

  const getChildSize = (child: React.ReactChild) => {
    if (child.hasOwnProperty('group')) {
      return groupLabelSize
    }

    return itemSize
  }

  const getHeight = () => {
    if (itemCount > numItemsInWindow) {
      return numItemsInWindow * itemSize
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0)
  }

  const gridRef = useResetCache(itemCount)

  const listHeight = getHeight() + 2 * LISTBOX_PADDING

  // get the group label ref or item ref for the first of each type of item in the list
  const getRefProps = (props) => {
    const refProps: {
      ref?: React.Ref<HTMLElement>
    } = {}
    const firstGroupLabelIndex = props.data.findIndex((i) =>
      i.hasOwnProperty('group')
    )
    const firstItemIndex = props.data.findIndex(
      (i) => !i.hasOwnProperty('group')
    )

    if (props.index === firstGroupLabelIndex) {
      refProps.ref = groupLabelRef
    } else if (props.index === firstItemIndex) {
      refProps.ref = itemRef
    }

    return refProps
  }

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={otherProps}>
        <VariableSizeList
          ref={gridRef}
          height={listHeight}
          innerElementType="ul"
          itemCount={itemCount}
          itemData={itemData}
          itemSize={(index) => getChildSize(itemData[index])}
          outerElementType={OuterElementType}
          overscanCount={overscanCount}
          width="100%"
        >
          {(props) => {
            // attach ref for first group label and first item so we can measure them
            const refProps = getRefProps(props)
            const { data, index, style } = props

            /*
             data[index] can be an option or a group header.
             - an option is the array passed in from Autocomplete#renderOption. Example: [ props: {}, option: {} ]
             - a header is a single object. The object doesn't appear to be used by the OptionComponent below
             */
            const optionOrGroupHeading = data[index]
            const [optionProps, option] = Array.isArray(optionOrGroupHeading)
              ? optionOrGroupHeading // option
              : [{}, {}] // group header

            const inlineStyle = {
              ...style,
              top: Number(style.top) + LISTBOX_PADDING
            }

            const isDisabled = optionProps['aria-disabled']

            // we seem to have to force this here
            // perhaps the virtualization affects the default hover styling
            const clickableStateCSS = isDisabled
              ? {
                  opacity: theme.opacity[0],
                  pointerEvents: 'none'
                }
              : {
                  '&:hover': {
                    cursor: 'pointer',
                    backgroundColor: theme.colors.gray[0]
                  }
                }

            return (
              <VirtualizedListbox {...props} {...refProps}>
                <OptionComponent
                  key={`virtualized-list-item-${
                    optionProps.key ?? optionProps.id
                  }`}
                  ref={ref}
                  css={{
                    ...inlineStyle,
                    paddingTop: 6,
                    paddingLeft: theme.space[5],
                    paddingRight: theme.space[2],
                    ...clickableStateCSS
                  }}
                  option={option}
                  props={optionProps}
                />
              </VirtualizedListbox>
            )
          }}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  )
})
